import { differenceInDays } from 'date-fns'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import { index } from '@last/core'
import { useTracker } from '@last/core-ui/paprika'

import { getBillingStatus, getDemoAuth, loginSupport } from '@/api/auth'
import { authApi, employeesApi, internalBillingApi } from '@/api/client'
import { getLocation } from '@/api/locations'
import app from '@/app'
import { useNotifications } from '@/composables/useNotifications'
import StorageFactory from '@/drivers/storage'
import { t } from '@/i18n'
import { logger, rum } from '@/monitoring'
import mqtt from '@/sync/mqtt'
import type { BillingStatus, Employee, Location, ReservationSubscription } from '@/types'

export const useAuthStore = defineStore(
  'auth',
  () => {
    const tracker = useTracker()
    const router = useRouter()

    const accessToken = ref<string>()
    const employees = ref<Record<string, Employee>>({})
    const privileges = ref<Record<string, string[]>>({})
    const currentEmployeeId = ref<string>()
    const locationId = ref<string>()
    const location = ref<{
      id: string
      name: string
      coordinates: { latitude: number; longitude: number }
    }>()
    const organizationId = ref<string>()
    const billingStatus = ref<BillingStatus>({
      stillInTrial: false,
      gracePeriodTriggered: false,
      posEnabled: true
    })
    const reservationBillingStatus = ref<ReservationSubscription>()

    const isAuthenticated = computed(() => !!accessToken.value)

    const listEmployees = computed(() => Object.values(employees.value || {}))

    const posAccessEmployees = computed(() =>
      listEmployees.value.filter(employee => employee.posAccess)
    )

    const currentEmployee = computed<Employee | null>(() => {
      if (!currentEmployeeId.value) return null
      return employees.value[currentEmployeeId.value]
    })

    const currentEmployeePrivileges = computed(() => {
      if (!currentEmployeeId.value) return []
      return privileges.value[currentEmployeeId.value]
    })

    const trialExpired = computed(() =>
      Boolean(billingStatus.value.stillInTrial && billingStatus.value.expired)
    )

    const gracePeriodExpired = computed(() =>
      Boolean(billingStatus.value.gracePeriodTriggered && billingStatus.value.expired)
    )

    watch(
      billingStatus,
      () => {
        const notification = useNotifications()
        if (billingStatus.value.stillInTrial) {
          notification.notifyError({
            id: 'trial-days',
            important: true,
            title: t('topbar.trial-days-left', {
              days: daysLeft.value
            })
          })
        }

        if (billingStatus.value.gracePeriodTriggered) {
          notification.notifyError({
            id: 'grace-period',
            important: true,
            title: t('topbar.grace-period-days-left', {
              days: daysLeft.value
            })
          })
        }
      },
      {
        immediate: true
      }
    )

    const daysLeft = computed(() => {
      if (!billingStatus.value.endsAt) return '-'
      return differenceInDays(new Date(billingStatus.value.endsAt), new Date())
    })

    const hasReservationSubscription = computed(() => {
      return !!reservationBillingStatus.value?.advancedSettings
    })

    async function login(data: { pin: string } | { email: string; password: string }) {
      const deviceInfo = await app.getDeviceInfo()
      const response = await authApi.login({
        loginRequest: {
          ...data,
          deviceInfo
        }
      })
      accessToken.value = response.accessToken
      if (response.locationId) {
        locationId.value = response.locationId
      }
    }

    function askPinForPrivilege(privilege: string) {
      return !posAccessEmployees.value.every(employee =>
        privileges.value[employee.id].includes(privilege)
      )
    }

    function hasPrivilege(employeeId: string, privilege: string) {
      return !!privileges.value[employeeId]?.includes(privilege)
    }

    function setAccessToken(token: string) {
      accessToken.value = token
    }

    function initAnalytics() {
      if (!location.value) return
      const { id, name } = location.value
      tracker.identify({
        id,
        location_id: id,
        name,
        organization_id: organizationId.value,
        accessToken: accessToken.value
      })
      rum.identify({ id, name })
      logger.identify({ id, name })
    }

    async function redirectLogin({ accessToken }: { accessToken: string }) {
      if (!accessToken) return false

      setAccessToken(accessToken)
      return true
    }

    async function supportLogin({
      accessToken,
      selectedOrganizationId,
      selectedLocationId
    }: {
      accessToken: string
      selectedOrganizationId: string
      selectedLocationId: string
    }) {
      const supportUserIsValid = await loginSupport(accessToken)
      if (supportUserIsValid) {
        setAccessToken(accessToken)
        organizationId.value = selectedOrganizationId
        locationId.value = selectedLocationId
        return true
      }
      return false
    }

    async function refreshCurrentLocation() {
      if (!locationId.value) return
      const refreshLocation = await getLocation()
      selectLocation(refreshLocation)
    }

    function selectEmployee(employeeId: string) {
      currentEmployeeId.value = employeeId
    }

    async function refreshEmployees() {
      const employeesData = await employeesApi.getEmployees()
      employees.value = index(employeesData, 'id')
      privileges.value = index(employeesData, 'id', employee => employee.privileges ?? [])
    }

    async function refreshBillingStatus() {
      billingStatus.value = await getBillingStatus()
      reservationBillingStatus.value = await internalBillingApi.getReservationsFeatures()
    }

    function validateEmployeePinWithPrivilege({
      pin,
      privilege
    }: {
      pin: string
      privilege: string
    }) {
      const ret = { pin: false, access: false }
      const currentEmployee = currentEmployeeId.value
        ? employees.value[currentEmployeeId.value]
        : null

      if (currentEmployee && pin === currentEmployee.accessPin) {
        ret.pin = true
        if (currentEmployeePrivileges.value.includes(privilege)) {
          ret.access = true
        }
      } else {
        listEmployees.value.forEach(employee => {
          if (employee.accessPin === pin && hasPrivilege(employee.id, privilege)) {
            ret.access = true
            currentEmployeeId.value = employee.id
          }
        })
      }
      return ret
    }

    function selectLocation(newLocation: Location) {
      locationId.value = newLocation.id
      location.value = {
        id: newLocation.id,
        name: newLocation.name,
        coordinates: {
          latitude: newLocation.latitude,
          longitude: newLocation.longitude
        }
      }
      mqtt.subscribe(newLocation.id)
      initAnalytics()
    }

    async function logout() {
      accessToken.value = undefined
      locationId.value = undefined
      await StorageFactory.getStorage().clear()
      router.push({ name: 'login' })
    }

    async function getDemoAuthData() {
      const demoData = await getDemoAuth()
      const { locationId: demoLocationId, organizationId: demoOrganizationId } = demoData
      locationId.value = demoLocationId
      organizationId.value = demoOrganizationId
      accessToken.value = 'demoToken'
    }

    return {
      accessToken,
      employees,
      privileges,
      currentEmployeeId,
      locationId,
      location,
      organizationId,
      billingStatus,

      isAuthenticated,
      listEmployees,
      posAccessEmployees,
      currentEmployee,
      currentEmployeePrivileges,
      trialExpired,
      gracePeriodExpired,
      hasReservationSubscription,

      login,
      askPinForPrivilege,
      setAccessToken,
      redirectLogin,
      supportLogin,
      refreshCurrentLocation,
      selectEmployee,
      refreshEmployees,
      refreshBillingStatus,
      validateEmployeePinWithPrivilege,
      selectLocation,
      logout,
      getDemoAuthData,
      hasPrivilege
    }
  },
  { persist: true }
)

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
